// File: typescript/apps/front-end/src/main/react/Dialog/InviteUserToGroupsDialogLazy.ts
//
// Lazy delegate for the person-first group picker's imperative opener.
//
// `UserButtons` is eager chrome — it renders on the profile header, user cards, the home discovery
// rail, group member rows and join-request rows — so a static import of the dialog would pull the
// picker, its tabs and its candidate types onto the startup graph every page has to parse (rule 27).
// Routed through `importChunkWithRetryAndReload` rather than a bare `import()` so a stale or failed
// chunk fetch retries and reloads instead of silently doing nothing (rule 26).
//
// Drop-in replacement for `import { showInviteUserToGroupsDialog } from ".../InviteUserToGroupsDialog"`.

import type { Localizer } from "@/main/react/Types";
import type { AxiosResponse } from "axios";
import type { GroupInviteCandidatesBootstrapResponse } from "@/main/common/GroupInviteCandidateTypes";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";

/**
 * `PROFILE-65` (FI-779): a bootstrap read the opener already made, handed through so the picker does not
 * read the same key again (one `/modal-bootstrap` read per open). Declared here rather than imported from
 * the dialog module so this eager file names nothing of the lazy chunk; the dialog's own
 * `InviteUserToGroupsDialogOptions` is the same shape.
 */
export type InviteUserToGroupsDialogHandOff = {
    bootstrap?: Promise<AxiosResponse<GroupInviteCandidatesBootstrapResponse>>;
};

export function showInviteUserToGroupsDialog(username: string, localizer: Localizer, options?: InviteUserToGroupsDialogHandOff) {
    void importChunkWithRetryAndReload(
        () => import("@/main/react/Dialog/InviteUserToGroupsDialog").then((m) => ({ default: m.showInviteUserToGroupsDialog })),
        "invite-user-to-groups-dialog",
    ).then((m) => m.default(username, localizer, options));
}
